// Widget panel look (Settings → Appearance → Widget style), as CSS custom properties.
// They are set on the widget layer only, so menus, pop-ups and messages keep the theme's
// solid panels even if the widgets have no background at all.

import type { CSSProperties } from 'react';
import type { ThemeId } from '../store/settings';

export interface WidgetStyle {
  background: boolean;
  /** Panel colour as #rrggbb; '' = the theme's own. */
  color: string;
  /** 0..1; null = the theme's own. */
  opacity: number | null;
  /** Frosted glass (backdrop blur). */
  blur: boolean;
  border: boolean;
  shadow: boolean;
  shadowColor: string;
  /** 0..1 */
  shadowStrength: number;
  /** Corner radius in px at display size M. */
  radius: number;
}

export const DEFAULT_WIDGET_STYLE: WidgetStyle = {
  background: true,
  color: '',
  opacity: null,
  blur: true,
  border: true,
  shadow: true,
  shadowColor: '#000000',
  shadowStrength: 0.45,
  radius: 20,
};

/** Each theme's panel colour (rgb) and transparency, normal and for Lite mode. */
export const THEME_PANEL: Record<ThemeId, { rgb: string; alpha: number; solid: number }> = {
  dark: { rgb: '15 23 42', alpha: 0.45, solid: 0.85 },
  light: { rgb: '255 255 255', alpha: 0.6, solid: 0.92 },
  oled: { rgb: '0 0 0', alpha: 0.55, solid: 1 },
  amber: { rgb: '41 26 8', alpha: 0.5, solid: 0.9 },
  contrast: { rgb: '0 0 0', alpha: 1, solid: 1 },
};

/** "#3b82f6" → "59 130 246"; null for anything else. */
export function hexToRgb(hex: string): string | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

export function panelVars(s: WidgetStyle, theme: ThemeId): CSSProperties {
  const t = THEME_PANEL[theme] ?? THEME_PANEL.dark;
  const rgb = (s.color && hexToRgb(s.color)) || t.rgb;
  const alpha = s.opacity ?? t.alpha;
  const shadowRgb = hexToRgb(s.shadowColor) ?? '0 0 0';
  return {
    '--panel': s.background ? `rgb(${rgb} / ${alpha})` : 'transparent',
    '--panel-lite': s.background ? `rgb(${rgb} / ${Math.max(alpha, t.solid)})` : 'transparent',
    '--panel-blur': s.background && s.blur ? 'blur(var(--blur)) saturate(140%)' : 'none',
    '--panel-border': s.border ? '1px solid var(--border)' : '1px solid transparent',
    '--panel-shadow': s.shadow ? `0 10px 40px rgb(${shadowRgb} / ${s.shadowStrength})` : 'none',
    '--panel-radius': `${Math.max(0, s.radius) / 16}rem`,
  } as CSSProperties;
}
